import { AfterViewChecked, AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import flatpickr from "flatpickr";
import { FlatpickrFormat } from '../../consts';
import { formatDate } from 'devextreme/localization';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import { OurDate } from '../../interfaces/general/our-date';
import { populateOurDate } from '../../functions-generic/functions-generic-date';

@Component({
  selector: 'app-dropdown-date',
  templateUrl: './dropdown-date.component.html',
  styleUrls: ['./dropdown-date.component.scss']
})

export class DropdownDateComponent implements AfterViewChecked, AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() allowUndefinedInitialDate : boolean=false;
  @Input() initialDate  : OurDate | undefined;
  @Input() dateFormat   : string  | undefined;
  @Input() disable      : boolean | undefined;
  @Input() enableTime   : boolean=false;
  @Input() monthSelector: boolean | undefined;
  @Input() title        : string  | undefined;
  @Input() yearSelector : boolean | undefined;
  
  @Output() clearedEvent       : EventEmitter<void>   =new EventEmitter<void>();
  @Output() selectOnChangeEvent: EventEmitter<OurDate>=new EventEmitter<OurDate>();
  
  @ViewChild('flatpickr')          _flatpickr!        : any;
  @ViewChild('flatpickrDirective') flatpickrDirective!: any;
  @ViewChild('flatpickrMonthOnly') flatpickrMonthOnly!: any;

  private altInputElement         : HTMLInputElement | undefined;
  private dateObject              : OurDate | undefined;
  private monthPicker             : any;
  private monthPickerLastSyncedUtc: number | undefined;
  private monthPickerSelectedMonth: number | undefined;
  private monthPickerSelectedYear : number | undefined;
  private monthPickerUserPicked   : boolean=false;
  
  private altInputBlurHandler     : ((e: FocusEvent    ) => void) | undefined;
  private altInputKeydownHandler  : ((e: KeyboardEvent ) => void) | undefined;
  private altInputPasteHandler    : ((e: ClipboardEvent) => void) | undefined;

  public dateForm!                : UntypedFormGroup;
  public yearSelectorOptions      : number[]=[];
  public yearSelectorValue        : number | undefined;

  constructor(
    private exceptionsService: ExceptionsService,
    private formBuilder      : FormBuilder
  ) {
    
  }

  ngOnInit(): void {
    try {
      if (this.initialDate==undefined && this.allowUndefinedInitialDate == false )
        throw new Error("DropdownDateComponent - InitialDate is undefined");

      if (this.title==undefined || this.title.trim()=="")
        this.title="Select Date";

      if (this.dateFormat==undefined)
        this.dateFormat=this.enableTime ? FlatpickrFormat.Day_MonthWordFull_Year_Time
                                        : FlatpickrFormat.Day_MonthWordFull_Year;

      if (this.yearSelector)
        this.buildYearSelector();

      this.dateForm = this.formBuilder.group({
        date: [this.initialDate ? (this.enableTime ? this.utcWallClockString(this.initialDate.date)
                                                   : formatDate(this.initialDate.date,'yyyy-MM-dd'))
                                : undefined, []]
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(){
    try {
      this.ensureMonthPickerState();

      if (this.disable != undefined && this.disable)
        this.dateForm.controls["date"].disable();

      this.attachAltInputRestrictions();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewChecked(): void {
    try {
      this.ensureMonthPickerState();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes["monthSelector"] || changes["initialDate"]) {
        if (changes["initialDate"] && !changes["initialDate"].firstChange)
          this.monthPickerUserPicked=false;

        this.ensureMonthPickerState();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroyMonthPicker();
      this.detachAltInputRestrictions();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private attachAltInputRestrictions(): void {
    try {
      const hiddenInput=this._flatpickr?.nativeElement  as HTMLInputElement | undefined;
      const altInput   =hiddenInput?.nextElementSibling as HTMLInputElement | null;

      if (!altInput || altInput.tagName !== 'INPUT')
        return;

      this.altInputElement=altInput;

      this.altInputKeydownHandler=(e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey || e.altKey)
          return;

        const navigationKeys=['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'];

        if (navigationKeys.indexOf(e.key) !== -1)
          return;

        const allowedKey=this.enableTime ? /^[0-9/: ]$/ : /^[0-9/]$/;

        if (!allowedKey.test(e.key))
          e.preventDefault();
      };

      this.altInputPasteHandler=(e: ClipboardEvent) => {
        const pasted      =e.clipboardData?.getData('text') ?? '';
        const allowedPaste=this.enableTime ? /^[0-9/: ]+$/ : /^[0-9/]+$/;

        if (!allowedPaste.test(pasted))
          e.preventDefault();
      };

      this.altInputBlurHandler=(_e: FocusEvent) => {
        this.commitTypedDate(altInput.value);
      };

      altInput.addEventListener('keydown', this.altInputKeydownHandler);
      altInput.addEventListener('paste',   this.altInputPasteHandler);
      altInput.addEventListener('blur',    this.altInputBlurHandler);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private detachAltInputRestrictions(): void {
    try {
      if (this.altInputElement && this.altInputKeydownHandler)
        this.altInputElement.removeEventListener('keydown', this.altInputKeydownHandler);

      if (this.altInputElement && this.altInputPasteHandler)
        this.altInputElement.removeEventListener('paste', this.altInputPasteHandler);

      if (this.altInputElement && this.altInputBlurHandler)
        this.altInputElement.removeEventListener('blur', this.altInputBlurHandler);

      this.altInputBlurHandler   =undefined;
      this.altInputElement       =undefined;
      this.altInputKeydownHandler=undefined;
      this.altInputPasteHandler  =undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildYearSelector(): void {
    try {
      const currentYear=new Date().getFullYear();
      const startYear  =currentYear-20;
      const endYear    =currentYear+20;

      this.yearSelectorOptions=[];

      for (let year=startYear; year<=endYear; year++)
        this.yearSelectorOptions.push(year);

      if (this.initialDate?.date) {
        const seededYear=new Date(this.initialDate.date).getUTCFullYear();

        if (!isNaN(seededYear)) {
          this.yearSelectorValue=seededYear;

          if (this.yearSelectorOptions.indexOf(seededYear)===-1) {
            this.yearSelectorOptions.push(seededYear);
            this.yearSelectorOptions.sort((a, b) => a-b);
          }
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private commitTypedDate(typed: string): void {
    try {
      const trimmed=typed?.trim() ?? '';

      if (trimmed === '')
        return;

      const fp       =this.flatpickrDirective?.instance;
      const altFormat=this.dateFormat ?? 'F j, Y';

      const parsed=this.parseDayMonthYear(trimmed) ?? (fp?.parseDate ? fp.parseDate(trimmed, altFormat) : undefined);

      if (!parsed || isNaN(parsed.getTime())) {
        this.altInputElement!.value='';
        return;
      }

      const formValue=this.dateForm.controls['date'].value;
      const current  =formValue ? new Date(formValue) : undefined;

      if (current
          && !isNaN(current.getTime())
          && current.getFullYear()===parsed.getFullYear()
          && current.getMonth()   ===parsed.getMonth()
          && current.getDate()    ===parsed.getDate()
          && (!this.enableTime || (current.getHours()  ===parsed.getHours()
                               &&  current.getMinutes()===parsed.getMinutes())))
        return;

      if (fp?.setDate) {
        fp.setDate(parsed, true);
        this.selectOnChange();
      } else {
        this.dateForm.controls['date'].setValue(parsed);
        this.selectOnChangeEvent.emit(populateOurDate(this.exceptionsService, parsed));
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private parseDayMonthYear(typed: string): Date | undefined {
    try {
      if ((this.dateFormat ?? '') !== FlatpickrFormat.Day_Month_Year)
        return undefined;

      const match=/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(typed.trim());

      if (!match)
        return undefined;

      const day  =parseInt(match[1], 10);
      const month=parseInt(match[2], 10);
      let   year =parseInt(match[3], 10);

      if (match[3].length===2)
        year+=2000;

      if (month<1 || month>12 || day<1 || day>31)
        return undefined;

      const parsed=new Date(year, month-1, day, 0, 0, 0, 0);

      if (parsed.getFullYear() !== year || parsed.getMonth() !== month-1 || parsed.getDate() !== day)
        return undefined;

      return parsed;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  private utcWallClockString(date: Date): string {
    try {
      const source=new Date(date);

      if (isNaN(source.getTime()))
        return '';

      const pad=(value: number): string => value<10 ? '0'+value : ''+value;

      return source.getUTCFullYear()
             +'-'+pad(source.getUTCMonth()+1)
             +'-'+pad(source.getUTCDate())
             +' '+pad(source.getUTCHours())
             +':'+pad(source.getUTCMinutes());
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  private utcInstantFromLocalWallClock(value: string): Date {
    try {
      const match=/^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})/.exec(value?.trim() ?? '');

      if (!match)
        return new Date(value);

      return new Date(Date.UTC(parseInt(match[1], 10),
                               parseInt(match[2], 10)-1,
                               parseInt(match[3], 10),
                               parseInt(match[4], 10),
                               parseInt(match[5], 10), 0, 0));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return new Date(value);
    }
  }

  private ensureMonthPickerState(): void {
    try {
      if (this.monthSelector) {
        if (this.flatpickrMonthOnly?.nativeElement && !this.monthPicker) {
          this.monthPicker=flatpickr(this.flatpickrMonthOnly.nativeElement, {
            defaultDate: this.localDateFromUtcParts(this.initialDate?.date),
            plugins    : [monthSelectPlugin({
              shorthand : true,
              dateFormat: "F Y",
              theme     : "light"
            })],
            onReady: (selectedDates) => {
              const selectedDate=selectedDates[0];

              if (selectedDate) {
                this.monthPickerSelectedMonth=selectedDate.getMonth();
                this.monthPickerSelectedYear =selectedDate.getFullYear();
                this.monthPickerLastSyncedUtc=Date.UTC(this.monthPickerSelectedYear, this.monthPickerSelectedMonth, 1);
              }
            },
            onChange: (selectedDates) => {
              const selectedDate=selectedDates[0];

              if (selectedDate) {
                this.monthPickerSelectedMonth=selectedDate.getMonth();
                this.monthPickerSelectedYear =selectedDate.getFullYear();
                this.monthPickerLastSyncedUtc=Date.UTC(this.monthPickerSelectedYear, this.monthPickerSelectedMonth, 1);
                this.monthPickerUserPicked   =true;
                this.selectOnChange();
              }
            }
          });
        } else if (this.monthPicker && this.initialDate?.date && !this.monthPickerUserPicked) {
          const syncDate =this.localDateFromUtcParts(this.initialDate.date)!;
          const syncMonth=syncDate.getMonth();
          const syncYear =syncDate.getFullYear();
          const syncUtc  =Date.UTC(syncYear, syncMonth, 1);

          //CRN_20260217 - Only resync date actually changed
          if (this.monthPickerLastSyncedUtc !== syncUtc) {
            this.monthPicker.setDate(syncDate, false);
            this.monthPickerSelectedMonth=syncMonth;
            this.monthPickerSelectedYear =syncYear;
            this.monthPickerLastSyncedUtc=syncUtc;
          }
        }
      } else {
        this.destroyMonthPicker();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private localDateFromUtcParts(date: Date | undefined): Date | undefined {
    try {
      if (date==undefined)
        return undefined;

      const source=new Date(date);

      if (isNaN(source.getTime()))
        return undefined;

      return new Date(source.getUTCFullYear(), source.getUTCMonth(), source.getUTCDate(), 0, 0, 0, 0);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  private destroyMonthPicker(): void {
    try {
      if (this.monthPicker) {
        this.monthPicker.destroy();
        this.monthPicker             =undefined;
        this.monthPickerLastSyncedUtc=undefined;
        this.monthPickerUserPicked   =false;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public selectOnChange() {
    try {
      //CRN_20250212 - This holds the previously selected date?! Makes no sense!
      //this.dateObject=populateOurDate(this.dateForm.controls["date"].value);
      
      if (this.yearSelector) {
        if (this.yearSelectorValue==undefined) {
          this.dateObject=undefined;
          this.clearedEvent.emit();
          return;
        }

        this.dateObject=populateOurDate(this.exceptionsService, new Date(Date.UTC(this.yearSelectorValue, 0, 1)));
        
      } else if (this.monthSelector) {
        const selectedDate=this.monthPicker?.selectedDates?.[0];

        if (selectedDate) {
          this.monthPickerSelectedMonth=selectedDate.getMonth();
          this.monthPickerSelectedYear =selectedDate.getFullYear();
        }

        this.dateObject=populateOurDate(this.exceptionsService, new Date(Date.UTC(<number>this.monthPickerSelectedYear, <number>this.monthPickerSelectedMonth, 1,)));
      } else if (this.enableTime) {
        this.dateObject=populateOurDate(this.exceptionsService,
                                        this.utcInstantFromLocalWallClock(this._flatpickr.nativeElement.value));
      } else {
        this.dateObject=populateOurDate(this.exceptionsService, this._flatpickr.nativeElement.value);
      }
      
      this.selectOnChangeEvent.emit(this.dateObject);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
}
